import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Terminal, ArrowRight, Calendar } from 'lucide-react';
interface QA {
  q: string;
  a: React.ReactNode;
}
const FAQS: QA[] = [{
  q: 'How much does GitHub Copilot cost for an enterprise?',
  a: <>
        GitHub Copilot Business costs <strong data-id="element-231">$19/user/month</strong> and
        Enterprise costs <strong data-id="element-232">$39/user/month</strong>. A 500-developer team
        on Enterprise runs over <strong data-id="element-233">$234,000 per year</strong> before volume
        discounts. Since June 1, 2026, both plans bill on token-based AI
        Credits, so the seat price includes a monthly credit allotment and heavy
        usage bills per token beyond it. Copilot Enterprise can also require
        GitHub Enterprise Cloud at $21/user, pushing real cost toward $60/user.
      </>
}, {
  q: 'How much does Cursor cost for a team?',
  a: <>
        Cursor Teams costs <strong data-id="element-234">$40 per Standard seat per month</strong>, or
        $32 with annual billing, with $20 of included usage per seat. Premium
        seats are $120/month for roughly 5x the usage. A 10-person team on
        Standard seats runs about <strong data-id="element-235">$3,840/year</strong> annual. Cursor
        costs roughly double Copilot Business at the team tier.
      </>
}, {
  q: 'How much does Claude Code cost per developer?',
  a: <>
        Claude Code averages around{' '}
        <strong data-id="element-236">$13 per developer per active day</strong> and{' '}
        <strong data-id="element-237">$150 to $250 per developer per month</strong> at scale,
        according to Anthropic's documentation, with 90% of developers under $30
        per active day. It is bundled in Claude Pro ($20/mo), Max ($100 and
        $200/mo), and Team Premium ($100/seat/mo), or billed per token through
        the API.
      </>
}, {
  q: 'Is consumption billing cheaper than per-seat billing?',
  a: <>
        <strong data-id="element-238">It depends entirely on usage.</strong> Consumption billing
        (Claude Code, and Copilot's agent usage since June 2026) is cheaper for
        light and variable users because you pay only for what you consume. It
        is more expensive and less predictable for heavy daily users, because
        there is no ceiling. Per-seat billing is predictable but wastes money on
        idle licenses. The cheapest model is the one matched to your team's
        actual usage pattern.
      </>
}, {
  q: 'What is idle seat waste?',
  a: <>
        <strong data-id="element-239">
          Idle seat waste is money spent on AI coding licenses for developers
          who are not actively generating code.
        </strong>{' '}
        Teams that buy a seat for every engineer by default routinely carry 20
        to 40% idle seats, because coding patterns vary widely. At $40/seat, a
        few dozen idle licenses is thousands of dollars a month. It is the
        single most common and most recoverable AI tooling cost.
      </>
}, {
  q: 'How do I track AI coding tool ROI?',
  a: <>
        <strong data-id="element-240">Tie tool usage to delivery outcomes, not raw output.</strong>{' '}
        Measure real utilization (who actually generates accepted code), then
        correlate it with PR cycle time and deployment frequency. Healthy ROI on
        AI coding tools runs 2.5x to 3.5x on average and 4x to 6x for top teams,
        but only when the cost denominator includes real token and usage-based
        spend, not just seat licenses. Counting lines of code is misleading
        because AI inflates volume without proving value.
      </>
}, {
  q: 'Why is AI coding spend so hard to forecast now?',
  a: <>
        <strong data-id="element-241">
          Because the whole category moved to token billing in 2026, so cost is
          now a function of behavior rather than headcount.
        </strong>{' '}
        GitHub Copilot switched to token credits on June 1, 2026. Cursor and
        Claude Code already billed on credit pools and tokens. Two developers on
        identical seats can cost very different amounts depending on how heavily
        they use agents and large context windows.
      </>
}, {
  q: 'Does tracking AI tool usage require access to source code?',
  a: <>
        <strong data-id="element-242">No, it should not.</strong> Proper utilization tracking reads
        commit metadata, PR cycle data, and API token consumption logs through
        read-only integrations with Git providers and tool APIs. It does not
        need to read the contents of your source code. Always confirm a vendor's
        data handling before connecting.
      </>
}, {
  q: 'What is the fastest way to cut AI coding tool costs?',
  a: <>
        <strong data-id="element-243">Audit utilization and reclaim idle seats first</strong>, because
        it is immediate and risk-free. Teams typically recover 20 to 40% of seat
        spend this way. Then set token alerts to catch consumption spikes, and
        right-size seat tiers so heavy users get higher allowances while light
        users drop to cheaper plans.
      </>
}];
export function Faq() {
  return <div className="bg-slate-950 min-h-screen" data-id="element-244">
      <main className="max-w-[1100px] mx-auto border-x border-slate-800" data-id="element-245">
        {/* Header channel */}
        <div className="p-8 md:p-12 lg:p-16 border-b border-slate-800 relative overflow-hidden" data-id="element-246">
          <div className="absolute inset-0 bg-dot-grid opacity-20 pointer-events-none" data-id="element-247" />
          <div className="relative z-10" data-id="element-248">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-900 border border-slate-800 text-slate-400 text-[10px] font-mono font-bold uppercase tracking-widest mb-8" data-id="element-249">
              <Terminal size={12} data-id="element-250" /> CH.07 / KNOWLEDGE_BASE
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-50 mb-6 tracking-tighter" data-id="element-251">
              AI Coding Tool Cost FAQ{' '}
              <span className="text-emerald-500 glow-text-emerald" data-id="element-252">(2026)</span>
            </h1>
            <p className="text-slate-400 font-mono text-sm leading-relaxed max-w-2xl" data-id="element-253">
              Each answer leads with the direct response in the first sentence,
              so it can stand alone if pulled into a search snippet or an AI
              answer. Numbers verified June 2026.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-[10px] font-mono uppercase tracking-widest text-slate-500" data-id="element-254">
              <span className="flex items-center gap-2" data-id="element-255">
                <Calendar size={12} className="text-emerald-500" data-id="element-256" />{' '}
                LAST_UPDATED: 2026-06-15
              </span>
              <span data-id="element-257">AUTHOR: TOKENMETER_BY_WAYDEV</span>
            </div>
          </div>
        </div>

        {/* Q&A entries */}
        <div className="divide-y divide-slate-800" data-id="element-258">
          {FAQS.map((item, idx) => <motion.section key={idx} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.3
        }} className="p-8 md:p-12 hover:bg-slate-900/20 transition-colors" aria-labelledby={`faq-q-${idx}`} data-id="element-259">
              <div className="flex items-start gap-4" data-id="element-260">
                <span className="font-mono text-xs text-emerald-500 mt-1.5 shrink-0" data-id="element-261">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div data-id="element-262">
                  <h2 id={`faq-q-${idx}`} className="text-xl md:text-2xl font-bold text-slate-50 mb-4 tracking-tight" data-id="element-263">
                    {item.q}
                  </h2>
                  <p className="text-slate-300 leading-relaxed text-base md:text-lg" data-id="element-264">
                    {item.a}
                  </p>
                </div>
              </div>
            </motion.section>)}
        </div>

        {/* Footnote */}
        <div className="p-8 md:p-12 border-t border-slate-800 bg-slate-900/30" data-id="element-265">
          <p className="text-xs font-mono text-slate-500 italic leading-relaxed" data-id="element-266">
            * Pricing and benchmark figures verified June 2026. Confirm current
            vendor pricing on official pages before budgeting.
          </p>
        </div>

        {/* CTA channel */}
        <div className="p-8 md:p-12 border-t border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-id="element-267">
          <div data-id="element-268">
            <h3 className="text-lg font-bold text-slate-50 mb-1" data-id="element-269">
              See the full pricing matrix.
            </h3>
            <p className="text-sm font-mono text-slate-400" data-id="element-270">
              Compare all 7 tools side by side, or estimate your spend.
            </p>
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-271">
            <Link to="/compare" className="inline-flex items-center gap-2 bg-slate-900 border border-slate-800 text-slate-200 px-5 py-3 font-mono text-xs uppercase tracking-widest hover:border-emerald-500/50 hover:text-emerald-400 transition-colors" data-id="element-272">
              [ COMPARE_TOOLS ] <ArrowRight size={14} data-id="element-273" />
            </Link>
            <a href="https://waydev.co/pricing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 text-slate-950 px-5 py-3 font-mono font-bold text-xs uppercase tracking-widest hover:bg-emerald-400 transition-colors glow-box-emerald" data-id="element-274">
              [ GET_TOKENMETER ] <ArrowRight size={14} data-id="element-275" />
            </a>
          </div>
        </div>
      </main>
    </div>;
}